"use client";

import useTranslate from "@app/_hooks/useTranslate";
import {
  updateCheckoutBuyer,
  updateCheckoutReceiver,
} from "@server/requests/checkout";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";

import { isAdyenCheckoutEnabled } from "@/config/checkout";
import type { MarketConfiguration } from "@/config/markets/const";

import { checkoutStore } from "../Checkout.store";
import {
  getAddressSchema,
  getDeliveryAddressSchema,
  getZipCodeOnlySchema,
  superRefinePhoneNumber,
  transformPhoneNumber,
} from "./Address.utils";

export function useUpdateCheckoutBuyer() {
  const zipCode = checkoutStore((state) => state.address.zipCode);

  return useMutation({
    mutationFn: updateCheckoutBuyer,
    mutationKey: ["updateCheckoutBuyer", zipCode],
  });
}

export function useUpdateCheckoutReciver() {
  const zipCode = checkoutStore(
    (state) => state.deliveryAddress.deliveryZipCode,
  );

  return useMutation({
    mutationFn: updateCheckoutReceiver,
    mutationKey: ["updateCheckoutReciver", zipCode],
  });
}

export function useMergedAddressSchema({
  marketConfig,
}: {
  marketConfig: MarketConfiguration;
}) {
  const t = useTranslate();
  const { code: marketCode } = marketConfig;
  const zipCodeOnlySchema = getZipCodeOnlySchema({ marketCode, t });
  const addressSchema = getAddressSchema({ marketCode, t });
  const deliveryAddressSchema = getDeliveryAddressSchema({
    marketCode,
    t,
  });

  const hasSeparateDeliveryAddress = checkoutStore(
    (state) => state.hasSeparateDeliveryAddress,
  );

  const mergedAddressSchema = useMemo(() => {
    if (isAdyenCheckoutEnabled({ marketConfig }) === false) {
      return zipCodeOnlySchema;
    }

    if (hasSeparateDeliveryAddress) {
      return addressSchema
        .merge(deliveryAddressSchema)
        .superRefine((data, ctx) => {
          superRefinePhoneNumber(data, ctx, {
            marketCode,
            t,
          });
        })
        .transform(transformPhoneNumber);
    }

    return addressSchema
      .superRefine((data, ctx) => {
        superRefinePhoneNumber(data, ctx, {
          marketCode,
          t,
        });
      })
      .transform(transformPhoneNumber);
  }, [
    marketConfig,
    hasSeparateDeliveryAddress,
    addressSchema,
    zipCodeOnlySchema,
    deliveryAddressSchema,
    marketCode,
    t,
  ]);

  return mergedAddressSchema;
}
